import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import LocalizedSEO from "../../components/common/LocalizedSEO";
import GoogleAnalytics from "../../components/common/GoogleAnalytics";
import CookieConsent from "../../components/cookies/CookieConsent";
/* =========================================================
   SYSTEM
   ========================================================= */

import ServerDownPage from "../system/ServerDownPage";
import MaintenancePage from "../system/MaintenancePage";
import EmailUnverifiedPage from "../system/auth/EmailUnverifiedPage";
import { redirectToServerDown } from "../../utils/serverStatus";

/* =========================================================
   PUBLIC
   ========================================================= */

import HomePage from "../public/home/HomePage";
import FaqPage from "../public/faq/FaqPage";
import ContactPage from "../public/contact/ContactPage";
import PrivacyPolicyPage from "../public/legal/PrivacyPolicyPage";
import TermsConditionsPage from "../public/legal/TermsConditionsPage";
import RegisterHBPage from "../public/registration/homembuyer/RegisterPageHomebuyer";
import RegisterLenderPage from "../public/registration/lender/RegistrerLenderPage";
import Callback from "../Callback";
import LoginRecoveryPage from "../public/login/LoginRecoveryPage";
import PostLoginRedirectPage from "../public/login/PostLoginRedirectPage";
import HomebuyerInvitationPage from "../public/invitation/HomebuyerInvitationPage";
import PlatformAdminInvitationPage from "../public/admin-invitation/PlatformAdminInvitationPage";
import AdminInvitationLoginPage from "../public/invitation/AdminInvitationLoginPage";

/* =========================================================
   ADMIN
   ========================================================= */

import { UnsavedChangesProvider } from "../admin/UnsavedChangesProvider";
import AdminProtectedRoute from "../../pages/admin/layout/AdminProtectedRoute";
import AdminLayout from "../admin/layout/AdminLayout";
import AdminDashboard from "../admin/dashboard/AdminDashboard";
import AdminPlatformActivity from "../admin/activity/AdminPlatformActivity";
import AdminRequestsPage from "../admin/requests/AdminRequestsPage";
import AdminReportingPage from "../admin/reporting/AdminReportingPage";
import AdminUsersPage from "../admin/users/AdminUsersPage";
import AdminUserProfilePage from "../admin/users/AdminUserProfilePage";
import AdminOrganisationsPage from "../admin/organisations/AdminOrganisationsPage";
import AdminOrganisationDetailPage from "../admin/organisations/AdminOrganisationDetailPage";
import AdminMarketRegionsPage from "../admin/market_regions/AdminMarketRegionsPage";
import AdminReferenceDataPage from "../admin/reference_data/AdminReferenceDataPage";
import AdminFundingSourcesPage from "../admin/funding-sources/AdminFundingSourcesPage";
import AdminAccountPage from "../admin/account/AdminAccountPage";

/* =========================================================
   HOMEBUYER
   ========================================================= */

import { HomebuyerOssConnectionGuard } from "../homebuyer/layout/HomebuyerRouteGuard";
import HomebuyerLayout from "../../pages/homebuyer/layout/HomebuyerLayout";
import HomebuyerDashboard from "../../pages/homebuyer/dashboard/HomebuyerDashboard";

import HomebuyerDossierIntro from "../homebuyer/dossier/HomebuyerDossierIntro";
import HomebuyerDossierLayout from "../homebuyer/layout/HomebuyerDossierLayout";
import HomebuyerPersonalInfo from "../homebuyer/dossier/steps/HomebuyerPersonalInfo";
import HomebuyerFinancialInfo from "../homebuyer/dossier/steps/HomebuyerFinacialInfo";
import HomebuyerDocuments from "../homebuyer/dossier/steps/HomebuyerDocuments";
import DocumentProcessingStep from "../homebuyer/dossier/steps/DocumentProcessingStep";
import HomebuyerExtractedData from "../homebuyer/dossier/steps/HomebuyerExtractedData";
import HomebuyerPropertyInformation from "../homebuyer/dossier/steps/HomebuyerPropertyInformation";
import HomebuyerReviewSubmit from "../homebuyer/dossier/steps/HomebuyerReviewSubmit";

import HomebuyerOssPropertyDetails from "../homebuyer/oss_connection/HomebuyerOssPropertyDetails";
import HomebuyerOssPartnersPage from "../homebuyer/oss_connection/HomebuyerOssPartnersPage";
import HomebuyerOssReviewSubmitPage from "../homebuyer/oss_connection/HomebuyerOssReviewSubmitPage";
import ExternalRenovationPage from "../homebuyer/oss_connection/ExternalRenovationPage";

import HomebuyerInvoicesPage from "../homebuyer/invoices/HomebuyerInvoicesPage";
import HomebuyerAccountPage from "../homebuyer/account/HomebuyerAccountPage";
import HomebuyerCommunicationPage from "../homebuyer/communication/HomebuyerCommunicationPage";

/* =========================================================
   LENDER
   ========================================================= */

import LenderLayout from "../lender/layout/LenderLayout";
import { LenderNavigationGuardProvider } from "../../components/layout/lender/LenderNavigationGuardContext";
import LenderDashboard from "../lender/dashboard/LenderDashboard";
import LoanCasesPage from "../lender/applications/LoanCasesPage";

import LenderManualLoanCaseLayout from "../lender/applications/application/LenderManualLoanCaseLayout";
import LenderManualPersonalInfo from "../lender/applications/application/LenderManualLoanCasePersonalInfo";
import LenderManualLoanCaseFinancialInfo from "../lender/applications/application/LenderManualLoanCaseFinancialInfo";
import LenderManualLoanCaseDocuments from "../lender/applications/application/LenderManualLoanCaseDocuments";
import LenderManualLoanCasePropertyInfo from "../lender/applications/application/LenderManualLoanCasePropertyInfo";
import LenderManualLoanCaseFinalLoanDetails from "../lender/applications/application/LenderManualLoanCaseFinalLoanDetails";
import LenderManualLoanCaseReviewSave from "../lender/applications/application/LenderManualLoanCaseReviewSave";

import LenderInvoicesPage from "../lender/invoices/LenderInvoicesPage";
import LenderCommunicationPage from "../lender/communication/LenderCommunicationPage";
import LenderAccountPage from "../lender/account/LenderAccountPage";
import LenderSubmitDecisionPage from "../lender/applications/submit_decision/LenderSubmitDecisionPage";
import LenderAdditionalLoanPage from "../lender/applications/LenderAdditionalLoanPage";

/* =========================================================
   OSS REGISTRATION
   ========================================================= */

import { OssAccessCodeGuard } from "../public/registration/oss/OssAccessCodeGuard";
import OssRegistrationLayout from "../public/registration/oss/OssRegistrationLayout";
import RegisterOssStep1Page from "../public/registration/oss/RegisterOssStep1Page";
import RegisterOssAccessCodePage from "../public/registration/oss/RegisterOssAccessCode";
import RegisterOssCompanyDetailsPage from "../public/registration/oss/RegisterOssCompanyDetailsPage";
import RegisterOssServicesPage from "../public/registration/oss/RegisterOssServicesPage";
import RegisterOssReviewSubmitPage from "../public/registration/oss/RegisterOssReviewSubmitPage";

/* =========================================================
   OSS
   ========================================================= */

import OssLayout from "../one-stop-shop/layout/OssLayout";
import OssDashboard from "../one-stop-shop/dashboard/OssDashboard";
import OssRequestsPage from "../one-stop-shop/requests/OssRequestsPage";
import OssRequestDetailPage from "../one-stop-shop/requests/OssRequestDetailPage";
import OssInvoicesPage from "../one-stop-shop/invoices/OssInvoicesPage";
import OssCommunicationPage from "../one-stop-shop/communication/OssCommunicationPage";
import OssAccountPage from "../one-stop-shop/account/OssAccountPage";
import OssProjectsPage from "../one-stop-shop/projects/OssProjectsPage";

/* =========================================================
   SHARED RENOVATION PROJECT WORKFLOW
   ========================================================= */

import RenovationProjectLayout from "../projects/layout/RenovationProjectLayout";

import RenovationProjectPropertyInformationStep from "../projects/steps/RenovationProjectPropertyInformationStep";
import RenovationProjectPreAssessmentStep from "../projects/steps/RenovationProjectPreAssessmentStep";
import RenovationProjectRenovationPlanStep from "../projects/steps/RenovationProjectRenovationPlanStep";
import RenovationProjectEnergyPerformanceCertificateStep from "../projects/steps/RenovationProjectEnergyPerformanceCertificateStep";
import RenovationProjectWorksBudgetStep from "../projects/steps/RenovationProjectWorksBudgetStep";
import RenovationProjectSubsidiesGrantsStep from "../projects/steps/RenovationProjectSubsidiesGrantsStep";
import RenovationProjectFinancingStep from "../projects/steps/RenovationProjectFinancingStep";
import RenovationProjectMonitoringStep from "../projects/steps/RenovationProjectMonitoringStep";

/* =========================================================
   PUBLIC SERVICES
   ========================================================= */
import SimulationPage from "../public/services/SimulationPage";
import SelectService from "../public/services/SelectService";
import LoanSimulator from "../public/services/loan-simulator/LoanSimulator";
import InstallmentsCalculator from "../public/services/monthly-installments-calculator/InstallmentsCalculator";
import RenCostEstimator from "../public/services/renovation-cost-estimator/RenCostEstimator";

const AUTH_CONNECTION_KEY = "legup_auth_connection";

const AUTH_RETURN_TO_KEY = "legup_auth_return_to";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const AUTH_CONNECTIONS = {
  HOMEBUYER: "LEGUP-Homebuyers",
  LENDER: "LEGUP-Lenders",
  OSS: "LEGUP-OSS",
};

function ProtectedRoute({ children, connection, requiredActor }) {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getAccessTokenSilently,
    user,
  } = useAuth0();

  const redirectStarted = React.useRef(false);

  const [checkingAccess, setCheckingAccess] = React.useState(true);

  const [accessAllowed, setAccessAllowed] = React.useState(false);

  /* =====================================================
     CURRENT DEEP LINK
     ===================================================== */

  const getCurrentReturnTo = () => {
    return (
      `${window.location.pathname}` +
      `${window.location.search || ""}` +
      `${window.location.hash || ""}`
    );
  };

  React.useEffect(() => {
    if (isLoading || redirectStarted.current) {
      return;
    }

    /* =====================================================
       START AUTHENTICATION
       ===================================================== */

    const startAuthentication = async () => {
      if (redirectStarted.current) {
        return;
      }

      redirectStarted.current = true;

      setCheckingAccess(true);

      setAccessAllowed(false);

      const returnTo = getCurrentReturnTo();

      if (connection) {
        window.sessionStorage.setItem(AUTH_CONNECTION_KEY, connection);
      }

      window.sessionStorage.setItem(AUTH_RETURN_TO_KEY, returnTo);

      try {
        await loginWithRedirect({
          authorizationParams: {
            prompt: "login",

            ...(connection
              ? {
                  connection,
                }
              : {}),
          },

          appState: {
            returnTo,
          },
        });
      } catch (error) {
        redirectStarted.current = false;

        setCheckingAccess(false);

        console.error("Could not start authentication:", error);
      }
    };

    /* =====================================================
       CHECK CURRENT USER
       ===================================================== */

    const checkAccess = async () => {
      /* ---------------------------------------------------
         NOT AUTHENTICATED
         --------------------------------------------------- */

      if (!isAuthenticated) {
        await startAuthentication();

        return;
      }

      // ---------------------------------------------------
      // EMAIL NOT VERIFIED
      // ---------------------------------------------------

      if (user?.email_verified !== true) {
        setAccessAllowed(false);
        setCheckingAccess(false);

        return;
      }

      /* ---------------------------------------------------
         VERIFY PLATFORM ACTOR
         --------------------------------------------------- */

      try {
        setCheckingAccess(true);

        const token = await getAccessTokenSilently();

        const response = await fetch(`${API_BASE}/api/me/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json().catch(() => ({}));

        /* -------------------------------------------------
           SERVER ERROR
           ------------------------------------------------- */

        if (response.status >= 500) {
          redirectToServerDown();

          return;
        }

        /* -------------------------------------------------
           SESSION INVALID
           ------------------------------------------------- */

        if (response.status === 401 || response.status === 403) {
          await startAuthentication();

          return;
        }

        if (!response.ok) {
          throw new Error(data?.detail || "Could not verify platform access.");
        }

        /* -------------------------------------------------
           USER NOT LINKED
           ------------------------------------------------- */

        if (data?.linked === false) {
          await startAuthentication();

          return;
        }

        /* -------------------------------------------------
           ACTUAL ACTOR
           ------------------------------------------------- */

        const actualActor = data?.is_platform_admin
          ? "PLATFORM_ADMIN"
          : data?.primary_actor;

        /* -------------------------------------------------
           WRONG ACTOR
           ------------------------------------------------- */

        if (requiredActor && actualActor !== requiredActor) {
          console.log("Wrong actor for route.", {
            actualActor,
            requiredActor,
            returnTo: getCurrentReturnTo(),
          });

          await startAuthentication();

          return;
        }

        /* -------------------------------------------------
           CORRECT ACTOR
           ------------------------------------------------- */

        if (connection) {
          window.sessionStorage.setItem(AUTH_CONNECTION_KEY, connection);
        }

        setAccessAllowed(true);

        setCheckingAccess(false);
      } catch (error) {
        console.error("Protected route access check failed:", error);

        redirectToServerDown();
      }
    };

    checkAccess();
  }, [
    isLoading,
    isAuthenticated,
    user?.email_verified,
    loginWithRedirect,
    getAccessTokenSilently,
    connection,
    requiredActor,
  ]);

  if (!isLoading && isAuthenticated && user?.email_verified !== true) {
    return <Navigate to="/email-unverified" replace />;
  }

  /* =====================================================
     INITIAL ACCESS CHECK
     ===================================================== */

  if (isLoading || !isAuthenticated || checkingAccess || !accessAllowed) {
    return (
      <div
        style={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#eef2ea",
        }}
      >
        <div className="hb-layout-loader">
          <span className="hb-layout-loader__ring" />

          <span className="hb-layout-loader__dot" />
        </div>
      </div>
    );
  }

  /* =====================================================
     ACCESS GRANTED
     ===================================================== */

  return children;
}

function HomePageRoute() {
  const location = useLocation();

  return <HomePage key={location.key} />;
}

const PUBLIC_COOKIE_ROUTES = new Set([
  "/faq",
  "/contact",
  "/privacy-policy",
  "/terms-and-conditions",
  "/services",
  "/simulations",
  "/register/homebuyer",
  "/register/lender",
  "/register/oss/step-1",
]);

function OtherPublicCookieConsent() {
  const { pathname } = useLocation();

  const normalizedPath = pathname.replace(/\/+$/, "") || "/";

  const isPublic =
    PUBLIC_COOKIE_ROUTES.has(normalizedPath) ||
    /^\/services\/(loan|installment|renCost)\/[^/]+$/.test(normalizedPath);

  if (!isPublic) {
    return null;
  }

  return <CookieConsent />;
}

/* =========================================================
   APPLICATION ROUTES
   ========================================================= */

export default function AppRoutes() {
  return (
    <>
      <LocalizedSEO />
      <GoogleAnalytics />
      <OtherPublicCookieConsent />
      <Routes>
        {/* =====================================================
          SYSTEM
          ===================================================== */}

        <Route path="/server-down" element={<ServerDownPage />} />

        <Route path="/maintenance" element={<MaintenancePage />} />

        <Route path="/email-unverified" element={<EmailUnverifiedPage />} />

        {/* =====================================================
          PUBLIC
          ===================================================== */}

        <Route path="/" element={<HomePageRoute />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-and-conditions" element={<TermsConditionsPage />} />
        <Route path="/register/homebuyer" element={<RegisterHBPage />} />

        <Route
          path="/invitation/:token"
          element={<HomebuyerInvitationPage />}
        />

        <Route
          path="/admin/invitation/:token"
          element={<PlatformAdminInvitationPage />}
        />
        <Route
          path="/admin-invitation-login"
          element={<AdminInvitationLoginPage />}
        />

        <Route path="/register/lender" element={<RegisterLenderPage />} />

        {/* =====================================================
          OSS REGISTRATION
          ===================================================== */}

        <Route path="/register/oss" element={<OssRegistrationLayout />}>
          <Route path="step-1" element={<RegisterOssStep1Page />} />

          <Route path="access-code" element={<RegisterOssAccessCodePage />} />

          <Route element={<OssAccessCodeGuard />}>
            <Route
              path="company-details"
              element={<RegisterOssCompanyDetailsPage />}
            />

            <Route path="services" element={<RegisterOssServicesPage />} />

            <Route
              path="review-submit"
              element={<RegisterOssReviewSubmitPage />}
            />
          </Route>
        </Route>

        {/* =====================================================
          AUTH
          ===================================================== */}

        <Route path="/callback" element={<Callback />} />
        <Route path="/login" element={<LoginRecoveryPage />} />

        <Route path="/post-login" element={<PostLoginRedirectPage />} />

        {/* =====================================================
    PUBLIC SERVICES
    ===================================================== */}

        <Route path="/services" element={<SelectService />} />

        <Route path="/simulations" element={<SimulationPage />} />

        <Route path="/services/loan/:region" element={<LoanSimulator />} />

        <Route
          path="/services/installment/:region"
          element={<InstallmentsCalculator />}
        />

        <Route
          path="/services/renCost/:region"
          element={<RenCostEstimator />}
        />

        {/* =====================================================
          ADMIN
          ===================================================== */}

        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <UnsavedChangesProvider>
                <AdminLayout />
              </UnsavedChangesProvider>
            </AdminProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />

          <Route path="activity" element={<AdminPlatformActivity />} />

          <Route path="requests" element={<AdminRequestsPage />} />

          <Route path="reporting" element={<AdminReportingPage />} />

          <Route path="users" element={<AdminUsersPage />} />

          <Route path="users/:userId" element={<AdminUserProfilePage />} />

          <Route path="organisations" element={<AdminOrganisationsPage />} />

          <Route
            path="organisations/:organisationId"
            element={<AdminOrganisationDetailPage />}
          />

          <Route path="market-regions" element={<AdminMarketRegionsPage />} />
          <Route path="reference-data" element={<AdminReferenceDataPage />} />
          <Route path="funding-sources" element={<AdminFundingSourcesPage />} />

          <Route path="account" element={<AdminAccountPage />} />
        </Route>

        {/* =====================================================
          HOMEBUYER
          ===================================================== */}

        <Route
          path="/hb"
          element={
            <ProtectedRoute
              connection={AUTH_CONNECTIONS.HOMEBUYER}
              requiredActor="HOMEOWNER"
            >
              <HomebuyerLayout />
            </ProtectedRoute>
          }
        >
          {/* -------------------------------------------------
            Dashboard
            ------------------------------------------------- */}

          <Route path="dashboard" element={<HomebuyerDashboard />} />

          {/* -------------------------------------------------
            Dossier
            ------------------------------------------------- */}

          <Route path="dossier" element={<HomebuyerDossierIntro />} />

          <Route path="dossier/form" element={<HomebuyerDossierLayout />}>
            <Route path="personal-info" element={<HomebuyerPersonalInfo />} />

            <Route path="financial-info" element={<HomebuyerFinancialInfo />} />

            <Route path="documents" element={<HomebuyerDocuments />} />

            <Route
              path="document-processing"
              element={<DocumentProcessingStep />}
            />

            <Route path="extracted-data" element={<HomebuyerExtractedData />} />

            <Route
              path="property-information"
              element={<HomebuyerPropertyInformation />}
            />

            <Route path="review-download" element={<HomebuyerReviewSubmit />} />
          </Route>

          {/* -------------------------------------------------
            OSS connection
            ------------------------------------------------- */}

          <Route element={<HomebuyerOssConnectionGuard />}>
            <Route
              path="oss/property-details"
              element={<HomebuyerOssPropertyDetails />}
            />

            <Route
              path="oss/partners/renovation"
              element={<HomebuyerOssPartnersPage />}
            />

            <Route
              path="oss/partners/pre-purchase"
              element={<HomebuyerOssPartnersPage />}
            />

            <Route
              path="oss/review-submit"
              element={<HomebuyerOssReviewSubmitPage />}
            />
          </Route>

          <Route
            path="renovation/external"
            element={<ExternalRenovationPage />}
          />

          {/* -------------------------------------------------
            SHARED RENOVATION PROJECT WORKFLOW
            ------------------------------------------------- */}

          <Route
            path="projects/:projectId"
            element={
              <RenovationProjectLayout
                actor="HB"
                basePath="/hb/projects"
                backPath="/hb/dashboard"
              />
            }
          >
            <Route
              index
              element={<Navigate to="property-information" replace />}
            />

            <Route
              path="property-information"
              element={<RenovationProjectPropertyInformationStep />}
            />

            <Route
              path="pre-assessment"
              element={<RenovationProjectPreAssessmentStep />}
            />
            <Route
              path="renovation-plan"
              element={<RenovationProjectRenovationPlanStep />}
            />
            <Route
              path="energy-performance-certificate"
              element={<RenovationProjectEnergyPerformanceCertificateStep />}
            />
            <Route
              path="home-renovation"
              element={<RenovationProjectWorksBudgetStep />}
            />
            <Route
              path="subsidies-grants"
              element={<RenovationProjectSubsidiesGrantsStep />}
            />
            <Route
              path="financing"
              element={<RenovationProjectFinancingStep />}
            />
            <Route
              path="monitoring"
              element={<RenovationProjectMonitoringStep />}
            />
          </Route>

          {/* -------------------------------------------------
            Other HB areas
            ------------------------------------------------- */}

          <Route path="invoices" element={<HomebuyerInvoicesPage />} />

          <Route
            path="communication"
            element={<HomebuyerCommunicationPage />}
          />

          <Route path="account" element={<HomebuyerAccountPage />} />
        </Route>

        {/* =====================================================
          LENDER
          ===================================================== */}

        <Route
          path="/lender"
          element={
            <ProtectedRoute
              connection={AUTH_CONNECTIONS.LENDER}
              requiredActor="LENDER"
            >
              <LenderNavigationGuardProvider>
                <LenderLayout />
              </LenderNavigationGuardProvider>
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<LenderDashboard />} />

          <Route path="loan-cases" element={<LoanCasesPage />} />

          <Route
            path="loan-cases/:loanCaseId/manual-entry"
            element={<LenderManualLoanCaseLayout />}
          >
            <Route index element={<Navigate to="personal-info" replace />} />

            <Route
              path="personal-info"
              element={<LenderManualPersonalInfo />}
            />

            <Route
              path="financial-info"
              element={<LenderManualLoanCaseFinancialInfo />}
            />

            <Route
              path="documents"
              element={<LenderManualLoanCaseDocuments />}
            />

            <Route
              path="property-info"
              element={<LenderManualLoanCasePropertyInfo />}
            />

            <Route
              path="final-loan-details"
              element={<LenderManualLoanCaseFinalLoanDetails />}
            />

            <Route
              path="review-save"
              element={<LenderManualLoanCaseReviewSave />}
            />
          </Route>

          <Route
            path="loan-cases/:loanCaseId/submit-decision"
            element={<LenderSubmitDecisionPage />}
          />
          <Route
            path="loan-cases/:loanCaseId/additional-loan"
            element={<LenderAdditionalLoanPage />}
          />

          <Route path="invoices" element={<LenderInvoicesPage />} />

          <Route path="communication" element={<LenderCommunicationPage />} />

          <Route path="account" element={<LenderAccountPage />} />
        </Route>

        {/* =====================================================
          OSS
          ===================================================== */}

        <Route
          path="/oss"
          element={
            <ProtectedRoute
              connection={AUTH_CONNECTIONS.OSS}
              requiredActor="OSS"
            >
              <OssLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<OssDashboard />} />

          <Route path="requests" element={<OssRequestsPage />} />

          <Route
            path="requests/:requestId"
            element={<OssRequestDetailPage />}
          />

          <Route path="communication" element={<OssCommunicationPage />} />

          <Route path="invoices" element={<OssInvoicesPage />} />

          {/* -------------------------------------------------
            OSS project list stays OSS-specific.
            ------------------------------------------------- */}

          <Route path="projects" element={<OssProjectsPage />} />

          {/* -------------------------------------------------
            SHARED RENOVATION PROJECT WORKFLOW
            ------------------------------------------------- */}

          <Route
            path="projects/:projectId"
            element={
              <RenovationProjectLayout
                actor="OSS"
                basePath="/oss/projects"
                backPath="/oss/projects"
              />
            }
          >
            <Route
              index
              element={<Navigate to="property-information" replace />}
            />

            <Route
              path="property-information"
              element={<RenovationProjectPropertyInformationStep />}
            />

            <Route
              path="pre-assessment"
              element={<RenovationProjectPreAssessmentStep />}
            />
            <Route
              path="renovation-plan"
              element={<RenovationProjectRenovationPlanStep />}
            />
            <Route
              path="energy-performance-certificate"
              element={<RenovationProjectEnergyPerformanceCertificateStep />}
            />
            <Route
              path="home-renovation"
              element={<RenovationProjectWorksBudgetStep />}
            />
            <Route
              path="subsidies-grants"
              element={<RenovationProjectSubsidiesGrantsStep />}
            />
            <Route
              path="financing"
              element={<RenovationProjectFinancingStep />}
            />
            <Route
              path="monitoring"
              element={<RenovationProjectMonitoringStep />}
            />
          </Route>
          <Route path="account" element={<OssAccountPage />} />
        </Route>
      </Routes>
    </>
  );
}
